// COMP-PATIENT-HEADER (AP-PAT-02، US-PAT-081، IA §3): سربرگ مشترک مراجعه‌کننده در همه صفحات عملیاتی. داده از
// GET /patients/{id}/summary: نام نمایشی (VIP با حروف اول)، سن، شماره پرونده، وضعیت هویت، نشان‌ها، برچسب‌ها و بخش‌های ماژولار
// (هر ماژول یک Widget با کلید بخش ثبت می‌کند — docs/08-engineering/patient-header-widgets.md). بخشی که کاربر مجوزش را ندارد از
// سرور نمی‌آید؛ ماژول کند در «partial» اعلام می‌شود و بقیه نمایش داده می‌شوند (AC-PAT-080.3).
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type IdentityStatus, type PatientSummaryEntry, type PatientSummarySection, type PatientTagView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, toJalaliDateTime } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { PatientBadgeChips, patKeys, usePatientSummary } from './common';
import { PATIENT_HEADER_WIDGETS } from './headerWidgets';

export const COMP_PATIENT_HEADER = 'COMP-PATIENT-HEADER';

const IDENTITY_COLOR: Record<IdentityStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  Unverified: 'default',
  Pending: 'info',
  ShahkarVerified: 'success',
  CivilRegistryVerified: 'success',
  Mismatch: 'error',
};

/** نشان وضعیت هویت (US-PAT-020). */
export function IdentityStatusChip({ status }: { status: IdentityStatus }) {
  const { t } = useTranslation('pat');
  return (
    <Chip
      size="small"
      variant={status === 'Unverified' ? 'outlined' : 'filled'}
      color={IDENTITY_COLOR[status]}
      label={t(`pat:identity.status.${status}`)}
      data-testid="identity-status"
    />
  );
}

/** برچسب‌های مراجعه‌کننده (US-PAT-060). */
export function PatientTagChips({ tags }: { tags: readonly PatientTagView[] }) {
  if (tags.length === 0) {
    return null;
  }
  return (
    <Stack direction="row" spacing={0.5} component="span" sx={{ flexWrap: 'wrap', rowGap: 0.5 }} data-testid="patient-tags">
      {tags.map((tag) => (
        <Chip
          key={tag.id}
          size="small"
          label={tag.label}
          variant="outlined"
          sx={{ borderColor: tag.color, '& .MuiChip-label': { color: 'text.primary' } }}
        />
      ))}
    </Stack>
  );
}

/** نمایش مقدار یک ورودی بخش بر اساس نوع (تاریخ شمسی، عدد، مبلغ؛ شناسه نمایش داده نمی‌شود). */
export function SummaryEntryValue({ entry }: { entry: PatientSummaryEntry }) {
  if (entry.value === null || entry.value === undefined) {
    return null;
  }
  switch (entry.kind) {
    case 'DateTime':
      return <span>{toJalaliDateTime(entry.value)}</span>;
    case 'Number':
    case 'MoneyMinor':
      return <bdi>{faDigits(entry.value)}</bdi>;
    case 'Id':
      return null;
    default:
      return <span>{entry.value}</span>;
  }
}

/** Widget پیش‌فرض برای بخشی که ماژولش Widget ثبت نکرده: فهرست کلید و مقدار. */
export function GenericSummaryWidget({ section }: { section: PatientSummarySection; patientId: string }) {
  const { t } = useTranslation();
  const entries = section.entries.filter((entry) => entry.kind !== 'Id');
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div">
        {t(`${section.source.toLowerCase()}:summary.${section.key}.title`, { defaultValue: section.key })}
      </Typography>
      {entries.length === 0 ? (
        <Typography variant="body2">{t('pat:header.none')}</Typography>
      ) : (
        entries.map((entry) => (
          <Typography key={entry.key} variant="body2">
            <SummaryEntryValue entry={entry} />
          </Typography>
        ))
      )}
    </Box>
  );
}

/** تصویر مراجعه‌کننده (GET .../photo با توکن؛ Object URL محلی). */
export function PatientPhoto({ patientId, hasPhoto, name, size = 48 }: { patientId: string; hasPhoto: boolean; name: string; size?: number }) {
  const { tenantApi, session } = useTenantSession();
  const photo = useQuery({
    queryKey: patKeys.photo(session?.tenantId, patientId),
    queryFn: async () => {
      const result = await tenantApi.GET('/api/v1/patients/patients/{id}/photo', {
        params: { path: { id: patientId } },
        parseAs: 'blob',
      });
      return result.response.ok ? (result.data as unknown as Blob) : null;
    },
    enabled: hasPhoto,
    staleTime: 300_000,
  });
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!photo.data || typeof URL.createObjectURL !== 'function') {
      setUrl(undefined);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(photo.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [photo.data]);
  return (
    <Avatar src={hasPhoto ? url : undefined} alt={name} sx={{ width: size, height: size }} data-testid="patient-photo">
      {name.slice(0, 1)}
    </Avatar>
  );
}

export interface PatientHeaderProps {
  patientId: string;
  /** فقط نام، شماره پرونده و نشان‌ها (برای صفحات فشرده مانند جزئیات نوبت). */
  compact?: boolean;
}

/** سربرگ مشترک مراجعه‌کننده. */
export function PatientHeader({ patientId, compact = false }: PatientHeaderProps) {
  const { t } = useTranslation('pat');
  const summary = usePatientSummary(patientId);
  if (summary.isPending) {
    return <Skeleton variant="rounded" height={compact ? 40 : 72} />;
  }
  if (summary.error) {
    // در صفحات دیگر (حالت فشرده) خطای سربرگ مانع کار نیست؛ پیوند پرونده همان صفحه کافی است.
    return compact ? null : <ErrorState error={summary.error} compact onRetry={() => void summary.refetch()} />;
  }
  const header = summary.data;
  const sections = [...header.sections].sort((a, b) => Number(a.order) - Number(b.order));
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-screen-id={COMP_PATIENT_HEADER} data-testid="patient-header">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flexShrink: 0 }}>
          {!compact && <PatientPhoto patientId={header.id} hasPhoto={header.hasPhoto} name={header.displayName} />}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" component="div" data-testid="patient-header-name" sx={{ fontWeight: 600 }}>
              {header.displayName}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Chip
                size="small"
                variant="outlined"
                label={t('pat:search.file_number', { number: faDigits(header.fileNumber) })}
                data-testid="patient-file-number"
              />
              {header.age !== null && header.age !== undefined && (
                <Chip size="small" variant="outlined" label={t('pat:search.age', { age: faDigits(header.age) })} />
              )}
              <IdentityStatusChip status={header.identityStatus} />
              <PatientBadgeChips badges={header.badges} guardianMissing={header.guardianMissing} />
            </Stack>
          </Box>
        </Stack>
        {!compact && (
          <Stack direction="row" spacing={3} sx={{ flex: 1, flexWrap: 'wrap', rowGap: 1 }} data-testid="patient-header-sections">
            <PatientTagChips tags={header.tags} />
            {sections.map((section) => {
              const Widget = PATIENT_HEADER_WIDGETS[section.key] ?? GenericSummaryWidget;
              return (
                <Box key={section.key} data-testid={`header-section-${section.key}`}>
                  <Widget section={section} patientId={header.id} />
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
      {header.partial.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }} data-testid="patient-header-partial">
          {t('pat:header.partial', { sources: header.partial.join(t('pat:list_separator')) })}
        </Alert>
      )}
    </Paper>
  );
}
